import { type Action, checkIfActionsIsValid } from '@0xtorch/core'
import type { Chain } from '../../../chain'
import type { Converter, LowerHex } from '../../../types'
import type { TransactionDecoded } from '../../../types/transactionDecoded'
import { createEvmActionSource, createEvmAddressId } from '../../../utils'
import { createActionsByFailAnalyzer } from './fail'
import { createActionsByGasAnalyzer } from './gas'
import { createActionsByGeneralAnalyzer } from './general'
import { createActionsByHyperCoreAnalyzer } from './hyperCore'
import { createActionsByJsonAnalyzer } from './json'
import type { JsonAnalyzer } from './json/type'
import { createActionsBySpamAnalyzer } from './spam'
import { createTokenTransfers } from './utils/createTokenTransfers'
import { createActionsByWrappedTokenAnalyzer } from './wrappedToken'

type CreateActionsParameters = {
  chain: Chain
  transaction: TransactionDecoded
  relatedAddresses: Set<LowerHex>
  converter?: Converter
  jsonAnalyzers: readonly JsonAnalyzer[]
}

export const createActions = async ({
  chain,
  transaction,
  relatedAddresses,
  converter,
  jsonAnalyzers,
}: CreateActionsParameters): Promise<readonly Action[]> => {
  const actions: Action[] = []
  const source = createEvmActionSource({
    chainId: transaction.chainId,
    transactionHash: transaction.hash,
  })

  // [every] gas analyzer
  const actionsByGasAnalyzer = createActionsByGasAnalyzer({
    chain,
    transaction,
    relatedAddresses,
    source,
  })
  if (actionsByGasAnalyzer !== undefined) {
    actions.push(...actionsByGasAnalyzer)
  }

  // [some] fail analyzer
  const actionsByFailAnalyzer = createActionsByFailAnalyzer({
    transaction,
    relatedAddresses,
    source,
  })
  if (actionsByFailAnalyzer !== undefined) {
    actions.push(...actionsByFailAnalyzer)
    return resetAccountActionsOrder(actions)
  }

  const transfers = createTokenTransfers({
    chain,
    transaction,
    relatedAddresses,
  })
  const accountIds = new Set(
    [...relatedAddresses].map((address) =>
      createEvmAddressId({
        chainId: transaction.chainId,
        address,
      }),
    ),
  )

  // [some] wrapped token analyzer
  const actionsByWrappedTokenAnalyzer = createActionsByWrappedTokenAnalyzer({
    chain,
    transaction,
    transfers,
    relatedAddresses,
    source,
  })
  if (actionsByWrappedTokenAnalyzer !== undefined) {
    actions.push(...actionsByWrappedTokenAnalyzer)
    return resetAccountActionsOrder(actions)
  }

  // [some] HyperCore (Hyperliquid) <-> HyperEVM transfer analyzer
  const actionsByHyperCoreAnalyzer = createActionsByHyperCoreAnalyzer({
    chain,
    transaction,
    transfers,
    source,
  })
  if (actionsByHyperCoreAnalyzer !== undefined) {
    actions.push(...actionsByHyperCoreAnalyzer)
    return resetAccountActionsOrder(actions)
  }

  // [some] converter
  if (converter !== undefined) {
    const actionsByConverter = await converter({
      nativeToken: chain.nativeToken,
      relatedAddresses,
      source,
      transaction,
    })
    // validation して問題なければ action を返す
    if (
      actionsByConverter !== undefined &&
      checkIfActionsIsValid({
        actions: actionsByConverter,
        accountIds,
        transfers,
      })
    ) {
      actions.push(...actionsByConverter)
      return resetAccountActionsOrder(actions)
    }
  }

  // [some] json analyzer
  const actionsByJsonAnalyzer = createActionsByJsonAnalyzer({
    chain,
    transaction,
    relatedAddresses,
    accountIds,
    jsonAnalyzers,
    transfers,
    source,
  })
  if (actionsByJsonAnalyzer !== undefined) {
    actions.push(...actionsByJsonAnalyzer)
    return resetAccountActionsOrder(actions)
  }

  // [some] spam analyzer
  const actionsBySpamAnalyzer = createActionsBySpamAnalyzer({
    transaction,
    transfers,
    accountIds,
    relatedAddresses,
    source,
  })
  if (actionsBySpamAnalyzer !== undefined) {
    actions.push(...actionsBySpamAnalyzer)
    return resetAccountActionsOrder(actions)
  }

  // [some] general analyzer
  const actionsByGeneralAnalyzer = createActionsByGeneralAnalyzer({
    transaction,
    transfers,
    accountIds,
    source,
  })
  actions.push(...actionsByGeneralAnalyzer)
  return resetAccountActionsOrder(actions)
}

const resetAccountActionsOrder = (
  actions: readonly Action[],
): readonly Action[] => {
  for (const [index, action] of actions.entries()) {
    action.order = index
  }
  return actions
}
